import React, { useMemo, useState, useRef } from "react";
import {
  Box, Stack, Typography, FormControl, FormLabel, RadioGroup,
  FormControlLabel, Radio, TextField, MenuItem, Button,
  CircularProgress, Checkbox,
} from "@mui/material";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import emailjs from "emailjs-com"; // ✅ uncommented
import ReCAPTCHA from "react-google-recaptcha";

const GOOGLE_SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbxyTJ76kf8gGcszBEyZ7DRgDqU60blcdlqc72ZPdDXN0o4FMFzjeEfMs2dLN3dAEhiD4g/exec";

// ✅ EmailJS config
const EMAILJS_SERVICE_ID  = "service_x0cp6w9";
const EMAILJS_PUBLIC_KEY  = "Ccohc43NYH647Mn2F";
const TEMPLATE_CLIENT     = "template_s96wz5p"; // → sends to client, admin BCC'd in template
const TEMPLATE_ADMIN      = "template_kbgbh8q"; // → sends to admin, CC consultant

// ✅ Consultant name → email map
const CONSULTANT_EMAILS = {
  "Umair Jamil Khan":       "umair@aspiretaxbook.com.au",
  "Muhammad Abid":          "abid@aspiretaxbook.com.au",
  "Harita Rajindra Karia":  "harita@aspiretaxbook.com.au",
  "Muhammad Shahid Jamil":  "shahid@aspiretaxbook.com.au",
};

// ✅ Unique and easy to follow referral numbers mapped to each consultant
const CONSULTANT_REFERRALS = {
  "Umair Jamil Khan":       "UJ-12345",
  "Muhammad Abid":          "MA-56789",
  "Harita Rajindra Karia":  "HR-43210",
  "Muhammad Shahid Jamil":  "MSJ-24680",
};

// Helper to resolve consultant name from either referral number or text input
const getConsultantName = (preferredConsultant, referralNumber) => {
  const ref = (referralNumber || "").trim().toUpperCase();
  if (ref) {
    for (const [name, code] of Object.entries(CONSULTANT_REFERRALS)) {
      if (code.toUpperCase() === ref) {
        return name;
      }
    }
  }

  const name = (preferredConsultant || "").trim().toUpperCase();
  if (name) {
    // Exact match check
    for (const officialName of Object.keys(CONSULTANT_EMAILS)) {
      if (officialName.toUpperCase() === name) {
        return officialName;
      }
    }
    // Partial match check
    for (const officialName of Object.keys(CONSULTANT_EMAILS)) {
      if (officialName.toUpperCase().includes(name) || name.includes(officialName.toUpperCase())) {
        return officialName;
      }
    }
  }

  return preferredConsultant || "";
};

const ADMIN_EMAIL = "Js.projectiqs@gmail.com";

const BookingPortal = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const recaptchaRef = useRef(null);

  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  const [formData, setFormData] = useState({
    service: "",
    fullName: "",
    email: "",
    contactNumber: "",
    dateOfBirth: null,
    taxFileNumber: "",
    address: "",
    abnNumber: "",
    businessName: "",
    preferredDate: null,
    preferredTime: "",
    preferredConsultant: "",
    referralNumber: "",
    agree: false,
  });

  const [errors, setErrors]               = useState({});
  const [touched, setTouched]             = useState({});
  const [preferredOpen, setPreferredOpen] = useState(false);
  // const [consultantOption, setConsultantOption] = useState("available");

  const isTaxService      = formData.service === "Tax Returns / BAS";
  const showBusinessFields =
    formData.service === "Tax Returns / BAS" ||
    formData.service === "Business Registration";
  const needsBusinessFields = showBusinessFields;

  const today   = dayjs();
  const maxDate = today.add(1, "month");

  const shouldDisableSunday = (date) => {
    const d = date.day();
    return d === 0; // 0 = Sunday
  };

  const timeSlots = useMemo(() => {
    if (!formData.preferredDate) return [];

    const slots = [];
    const dateObj = dayjs(formData.preferredDate);
    const dayOfWeek = dateObj.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

    let startMinutes = 10 * 60; // Mon-Fri 10:00 AM
    let endMinutes = 18 * 60;   // Mon-Fri 06:00 PM

    if (dayOfWeek === 6) { // Saturday
      startMinutes = 11 * 60;   // Sat 11:00 AM
      endMinutes = 15 * 60;     // Sat 03:00 PM
    }

    let minutes = startMinutes;
    while (minutes < endMinutes) {
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      slots.push(dayjs().hour(h).minute(m).second(0).format("hh:mm A"));
      minutes += 30;
    }
    return slots;
  }, [formData.preferredDate]);

  const validate = (data, token) => {
    const e = {};
    if (!data.service)              e.service       = "Service is required";
    if (!data.fullName?.trim())     e.fullName      = "Full Name is required";
    if (!data.email?.trim())        e.email         = "Email is required";
    if (!data.contactNumber?.trim()) {
      e.contactNumber = "Contact Number is required";
    } else if (!/^\d+$/.test(data.contactNumber)) {
      e.contactNumber = "Contact Number must only contain numbers";
    }
    if (isTaxService) {
      if (!data.dateOfBirth)          e.dateOfBirth   = "Date of Birth is required";
      if (!data.taxFileNumber?.trim()) {
        e.taxFileNumber = "Tax File Number is required";
      } else if (!/^\d+$/.test(data.taxFileNumber.trim())) {
        e.taxFileNumber = "Tax File Number must only contain numbers";
      } else if (
        data.taxFileNumber.trim().length !== 8 &&
        data.taxFileNumber.trim().length !== 9
      ) {
        e.taxFileNumber = "Tax File Number must be 8 or 9 digits";
      }
      if (!data.address?.trim())    e.address       = "Address is required";
      if (!data.agree)              e.agree         = "You must agree to the terms";
    }
    if (showBusinessFields && data.abnNumber?.trim()) {
      if (!/^\d{11}$/.test(data.abnNumber)) {
        e.abnNumber = "ABN must be exactly 11 digits";
      }
    }
    if (!token) {
      e.recaptcha = "Please verify that you are not a robot";
    }
    return e;
  };

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const markTouched = (name) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    const e = validate({ ...formData }, recaptchaToken);
    setErrors((prev) => ({ ...prev, [name]: e[name] || "" }));
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token || "");
    if (token && errors.recaptcha) {
      setErrors((prev) => ({ ...prev, recaptcha: "" }));
    }
  };

  // ✅ Builds the shared template variables object
  const buildEmailVars = () => {
    const nameParts = formData.fullName.trim().split(/\s+/);
    const initials  = nameParts
      .map((n) => n[0] ?? "")
      .join("")
      .toUpperCase()
      .slice(0, 2) || "?";

    // Format current submission time in Australian time zone (Australia/Sydney)
    let submittedAt = "";
    try {
      const formatter = new Intl.DateTimeFormat("en-AU", {
        timeZone: "Australia/Sydney",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      submittedAt = formatter.format(new Date()).replace(/am|pm/i, (m) => m.toUpperCase());
    } catch (err) {
      submittedAt = dayjs().format("DD MMM YYYY, hh:mm A");
    }

    const resolvedConsultant = getConsultantName(formData.preferredConsultant, formData.referralNumber);

    return {
      // Identity
      fullName:            formData.fullName,
      email:               formData.email,
      initials,
      service:             formData.service,
      submittedAt,

      // Contact
      contactNumber:       formData.contactNumber,
      address:             formData.address             || "",

      // Tax & business
      dateOfBirth:         formData.dateOfBirth
                             ? dayjs(formData.dateOfBirth).format("DD-MM-YYYY") : "",
      taxFileNumber:       formData.taxFileNumber       || "",
      abnNumber:           formData.abnNumber           || "",
      businessName:        formData.businessName        || "",

      // Appointment
      preferredDate:       formData.preferredDate
                             ? dayjs(formData.preferredDate).format("DD MMM YYYY") : "Not specified",
      preferredTime:       formData.preferredTime       || "Not specified",
      preferredConsultant: resolvedConsultant           || "Any available",
      referralNumber:      formData.referralNumber      || "",

      // Terms
      agreedToTerms:       formData.agree ? "Yes" : "No",
    };
  };

  const handleReferralChange = (e) => {
    const val = e.target.value;
    setField("referralNumber", val);
    
    // Auto-fill preferred consultant if referral code matches a consultant
    const normalized = val.trim().toUpperCase();
    for (const [name, code] of Object.entries(CONSULTANT_REFERRALS)) {
      if (code.toUpperCase() === normalized) {
        setField("preferredConsultant", name);
        break;
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const eAll = validate(formData, recaptchaToken);
    setErrors(
      Object.fromEntries(Object.entries(eAll).filter(([k]) => touched[k] || true))
    );
    if (Object.keys(eAll).length > 0) return;

    setIsSubmitting(true);
    try {
      // ── 1. Google Sheets ──────────────────────────────────────────────────
      const payload = new FormData();
      payload.append("Service",       formData.service);
      payload.append("Full Name",     formData.fullName);
      payload.append("Email",         formData.email);
      payload.append("Contact Number", formData.contactNumber);

      if (isTaxService) {
        payload.append("Date Of Birth",    formData.dateOfBirth
          ? dayjs(formData.dateOfBirth).format("YYYY-MM-DD") : "");
        payload.append("Tax File Number",  formData.taxFileNumber);
        payload.append("Address",          formData.address);
      } else {
        payload.append("Date Of Birth",    "");
        payload.append("Tax File Number",  "");
        payload.append("Address",          "");
      }

      payload.append("ABN",
        needsBusinessFields ? (formData.abnNumber   || "") : "");
      payload.append("Business Name",
        needsBusinessFields ? (formData.businessName || "") : "");
      payload.append("Preferred Date",
        formData.preferredDate
          ? dayjs(formData.preferredDate).format("YYYY-MM-DD") : "");
      payload.append("Preferred Time",               formData.preferredTime       || "");
      
      const resolvedConsultant = getConsultantName(formData.preferredConsultant, formData.referralNumber);
      payload.append("Preferred Tax Consultant Name", resolvedConsultant);
      payload.append("Referral Number",               formData.referralNumber || "");
      payload.append("Agreed To Terms",              formData.agree ? "Yes" : "No");

      const res  = await fetch(GOOGLE_SCRIPT_URL, { method: "POST", body: payload });
      const json = await res.json();
      if (json?.result !== "success") throw new Error(json?.error || "Submission failed");

      // ── 2. EmailJS: client confirmation ──────────────────────────────────
      // Template field "To Email" is {{to_email}}, admin is already BCC'd in template
      try {
        const emailVars = { ...buildEmailVars(), to_email: formData.email };
        console.log("📨 Sending client email with variables:", emailVars);
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          TEMPLATE_CLIENT,
          emailVars,
          EMAILJS_PUBLIC_KEY
        );
      } catch (emailErr) {
        console.error("Client email failed:", emailErr);
        // don't block — still send admin email
      }
      // ── 3. EmailJS: admin + consultant notification ───────────────────────
      // Template field "To Email" is {{admin_email}}, "Cc" is {{consultantEmail}}
      try {
        const resolvedConsultant = getConsultantName(formData.preferredConsultant, formData.referralNumber);
        const consultantEmail = CONSULTANT_EMAILS[resolvedConsultant] ?? "";
        const adminVars = {
          ...buildEmailVars(),
          admin_email:    ADMIN_EMAIL,
          consultantEmail,
        };
        console.log("📨 Sending admin email with variables:", adminVars);
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          TEMPLATE_ADMIN,
          adminVars,
          EMAILJS_PUBLIC_KEY
        );
      } catch (emailErr) {
        console.error("Admin email failed:", emailErr.text ?? emailErr);
      }
      alert("Booking submitted successfully! We will reach out to you shortly.");

      // ── Reset ─────────────────────────────────────────────────────────────
      setFormData({
        service: "", fullName: "", email: "", contactNumber: "",
        dateOfBirth: null, taxFileNumber: "", address: "",
        abnNumber: "", businessName: "",
        preferredDate: null, preferredTime: "", preferredConsultant: "",
        referralNumber: "",
        agree: false,
      });
      // setConsultantOption("available");
      setErrors({});
      setTouched({});
      setRecaptchaToken("");
      recaptchaRef.current?.reset();
    } catch (err) {
      alert(err.message || "Submission failed");
      setRecaptchaToken("");
      recaptchaRef.current?.reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // JSX (unchanged from your original)
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 980, mx: "auto", px: { xs: 2, md: 3 }, py: 6 }}>
      <Typography sx={{ fontSize: { xs: 44, md: 72 }, fontWeight: 800, color: "#0b2e5e" }}>
        Booking Portal
      </Typography>

      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 4 }}>
        {/* SERVICE */}
        <FormControl>
          <FormLabel sx={{ fontWeight: 700 }}>
            Select your Service <span style={{ color: "red" }}>*</span>
          </FormLabel>
          <RadioGroup
            value={formData.service}
            onChange={(ev) => {
              const newService = ev.target.value;
              if (!["Tax Returns / BAS", "Business Registration"].includes(newService)) {
                setFormData((prev) => ({
                  ...prev,
                  service: newService,
                  dateOfBirth: null, taxFileNumber: "", address: "",
                  preferredDate: null, preferredTime: "", preferredConsultant: "",
                  referralNumber: "",
                  abnNumber: "", businessName: "",
                }));
                // setConsultantOption("available");
              } else {
                setFormData((prev) => ({ ...prev, service: newService }));
              }
              if (errors.service) setErrors((prev) => ({ ...prev, service: "" }));
            }}
            onBlur={() => markTouched("service")}
          >
            {[
              "Tax Returns / BAS",
              "Accounting and Bookkeeping Services",
              "Business Registration",
              "Payroll and Superannuation Setup",
            ].map((s) => (
              <FormControlLabel key={s} value={s} control={<Radio />} label={s} />
            ))}
          </RadioGroup>
          {errors.service && (
            <Typography color="error" variant="caption">{errors.service}</Typography>
          )}
        </FormControl>

        {/* FULL NAME */}
        <Box sx={{ mt: 3 }}>
          <Typography sx={{ fontWeight: 700 }}>
            Full Name <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            value={formData.fullName}
            onChange={(e) => setField("fullName", e.target.value)}
            onBlur={() => markTouched("fullName")}
            error={!!errors.fullName}
            helperText={errors.fullName}
          />
        </Box>

        {/* EMAIL + MOBILE */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700 }}>
              Email <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth type="email"
              value={formData.email}
              onChange={(e) => setField("email", e.target.value)}
              onBlur={() => markTouched("email")}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700 }}>
              Mobile <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              value={formData.contactNumber}
              onChange={(e) => setField("contactNumber", e.target.value)}
              onBlur={() => markTouched("contactNumber")}
              error={!!errors.contactNumber}
              helperText={errors.contactNumber}
            />
          </Box>
        </Stack>

        {/* TAX RETURNS / BAS EXTRA FIELDS */}
        {isTaxService && (
          <>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700 }}>
                  Date Of Birth <span style={{ color: "red" }}>*</span>
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format="DD-MM-YYYY"
                    value={formData.dateOfBirth ? dayjs(formData.dateOfBirth) : null}
                    onChange={(val) => setField("dateOfBirth", val ? val.toDate() : null)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.dateOfBirth,
                        helperText: errors.dateOfBirth,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700 }}>
                  Tax File Number <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  value={formData.taxFileNumber}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/\D/g, "");
                    setField("taxFileNumber", numericValue);
                  }}
                  onBlur={() => markTouched("taxFileNumber")}
                  error={!!errors.taxFileNumber}
                  helperText={errors.taxFileNumber}
                />
              </Box>
            </Stack>

            <Box sx={{ mt: 3 }}>
              <Typography sx={{ fontWeight: 700 }}>
                Address <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth multiline minRows={4}
                value={formData.address}
                onChange={(e) => setField("address", e.target.value)}
                onBlur={() => markTouched("address")}
                error={!!errors.address}
                helperText={errors.address}
              />
            </Box>

            <Typography sx={{ mt: 4, fontWeight: 800 }}>Appointment Details</Typography>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700 }}>Preferred Date</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={formData.preferredDate ? dayjs(formData.preferredDate) : null}
                    open={preferredOpen}
                    onOpen={() => setPreferredOpen(true)}
                    onClose={() => setPreferredOpen(false)}
                    onChange={(val) => {
                      setFormData((prev) => ({
                        ...prev,
                        preferredDate: val ? val.toDate() : null,
                        preferredTime: "",
                      }));
                      setPreferredOpen(false);
                    }}
                    closeOnSelect
                    minDate={today}
                    maxDate={maxDate}
                    shouldDisableDate={shouldDisableSunday}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        onClick: () => setPreferredOpen(true),
                        inputProps: { readOnly: true },
                        onKeyDown: (e) => e.preventDefault(),
                        sx: { cursor: "pointer" },
                      },
                    }}
                  />
                </LocalizationProvider>
                <Typography variant="caption" color="text.secondary">
                  (Sundays disabled • up to 1 month ahead)
                </Typography>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700 }}>Preferred Time</Typography>
                <TextField
                  select fullWidth
                  value={formData.preferredTime}
                  onChange={(e) => setField("preferredTime", e.target.value)}
                  disabled={!formData.preferredDate}
                >
                  <MenuItem value="" disabled>Select Time</MenuItem>
                  {timeSlots.map((slot) => (
                    <MenuItem key={slot} value={slot}>{slot}</MenuItem>
                  ))}
                </TextField>
                <Typography variant="caption" color="text.secondary">
                  (Enabled after date selection • Mon-Fri: 10:00 AM to 6:00 PM, Sat: 11:00 AM to 3:00 PM)
                </Typography>
              </Box>
            </Stack>

            {/* Preferred Consultant Name Dropdown commented out as requested */}
            {/*
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>
                Preferred Tax Consultant Name
              </Typography>
              <RadioGroup
                row value={consultantOption}
                onChange={(e) => {
                  setConsultantOption(e.target.value);
                  setField("preferredConsultant", "");
                }}
              >
                <FormControlLabel value="available" control={<Radio />} label="Select from available consultants" />
                <FormControlLabel value="other"     control={<Radio />} label="Other" />
              </RadioGroup>

              {consultantOption === "available" && (
                <TextField
                  select fullWidth sx={{ mt: 1.5 }}
                  value={formData.preferredConsultant}
                  onChange={(e) => setField("preferredConsultant", e.target.value)}
                >
                  <MenuItem value="" disabled>Select Consultant</MenuItem>
                  {["Umair Jamil Khan", "Muhammad Abid", "Harita Rajindra Karia", "Muhammad Shahid Jamil"].map(
                    (name) => <MenuItem key={name} value={name}>{name}</MenuItem>
                  )}
                </TextField>
              )}

              {consultantOption === "other" && (
                <TextField
                  fullWidth sx={{ mt: 1.5 }}
                  placeholder="Type consultant name (Otherwise type as ATB)"
                  value={formData.preferredConsultant}
                  onChange={(e) => setField("preferredConsultant", e.target.value)}
                />
              )}
            </Box>
            */}

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700 }}>Preferred Tax Consultant Name</Typography>
                <TextField
                  fullWidth sx={{ mt: 1 }}
                  placeholder="Type consultant name (Otherwise type as ATB)"
                  value={formData.preferredConsultant}
                  onChange={(e) => setField("preferredConsultant", e.target.value)}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700 }}>Referral Number (Optional)</Typography>
                <TextField
                  fullWidth sx={{ mt: 1 }}
                  placeholder="e.g. UJ-12345"
                  value={formData.referralNumber}
                  onChange={handleReferralChange}
                />
              </Box>
            </Stack>
          </>
        )}

        {/* ABN / BUSINESS NAME */}
        {showBusinessFields && (
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700 }}>ABN</Typography>
              <TextField
                fullWidth
                value={formData.abnNumber}
                onChange={(e) => setField("abnNumber", e.target.value)}
                onBlur={() => markTouched("abnNumber")}
                error={!!errors.abnNumber}
                helperText={errors.abnNumber}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700 }}>Business Name</Typography>
              <TextField
                fullWidth
                value={formData.businessName}
                onChange={(e) => setField("businessName", e.target.value)}
              />
            </Box>
          </Stack>
        )}

        {/* TERMS */}
        {isTaxService && (
          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.agree}
                  onChange={(e) => {
                    setField("agree", e.target.checked);
                    setTouched((p) => ({ ...p, agree: true }));
                  }}
                />
              }
              label={
                <span>
                  I hereby give authorisation to Aspire Tax and Bookkeeping Pty Ltd, to act on
                  my behalf as the registered Tax agent, and add me as a client to the online
                  services for agents.<span style={{ color: "red" }}> *</span>
                </span>
              }
            />
            {errors.agree && (
              <Typography color="error" variant="caption">{errors.agree}</Typography>
            )}
          </Box>
        )}

        {/* reCAPTCHA */}
        <Box sx={{ mt: 3, mb: 1 }}>
          {recaptchaSiteKey ? (
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={recaptchaSiteKey}
              onChange={handleRecaptchaChange}
              onExpired={() => handleRecaptchaChange("")}
            />
          ) : (
            <Typography color="error" variant="body2">
              reCAPTCHA Site Key is not configured. Please add `REACT_APP_RECAPTCHA_SITE_KEY` to your `.env.local` file and restart the development server.
            </Typography>
          )}
          {errors.recaptcha && (
            <Typography color="error" variant="caption" sx={{ display: "block", mt: 1 }}>
              {errors.recaptcha}
            </Typography>
          )}
        </Box>

        {/* SUBMIT */}
        <Box sx={{ mt: 3 }}>
          <Button
            type="submit" variant="contained" disabled={isSubmitting}
            sx={{
              px: 4, borderRadius: 999,
              backgroundColor: "#6a9b2f",
              "&:hover": { backgroundColor: "#5b8628" },
            }}
          >
            {isSubmitting ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={18} color="inherit" />
                <span>Submitting...</span>
              </Stack>
            ) : "Book Now"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default BookingPortal;